import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Article, StrapiData } from "../types";
import { useRouter } from "../context/RouterContext";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { api } from "../services/api";
import { useLocale } from "../context/LocaleContext";
import { ShareButton } from "../components/ShareButton";
import { TableOfContents } from "../components/TableOfContents";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";

export function ArticlePage() {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const { params, navigate } = useRouter();
  const { locale } = useLocale();

  useEffect(() => {
    async function fetchArticle() {
      //console.log("ArticlePage - params:", params);
      //console.log("ArticlePage - slug:", params.slug);

      if (!params.slug) {
        //console.log("No slug found, navigating to articles");
        navigate("articles");
        return;
      }

      try {
        //console.log("Fetching article with slug:", params.slug);
        const response: StrapiData<Article[]> = await api.getArticleBySlug(
          params.slug,
          locale
        );
        //console.log("Article response:", response);
        if (response.data && response.data.length > 0) {
          setArticle(response.data[0]);
        } else {
          //console.log("No article found, navigating to articles");
          navigate("articles");
        }
      } catch (error) {
        console.error("Failed to fetch article:", error);
        navigate("articles");
      } finally {
        setLoading(false);
      }
    }

    fetchArticle();
  }, [params.slug, locale, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Full Width Cover Image */}
      {article?.cover?.url && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full"
        >
          <img
            src={`${
              process.env.REACT_APP_STRAPI_URL ||
              "https://devops.caglayagmur.dev/api"
            }${article.cover.url}`}
            alt={article.cover.alternativeText || article.title}
            className="pt-8 w-full h-auto max-h-[250px] object-cover"
          />
        </motion.div>
      )}

      <div className="container mx-auto px-4 py-12">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -5 }}
          onClick={() => navigate("articles")}
          className="flex items-center gap-2 text-gray-400 hover:text-red-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          {locale === "tr-TR" ? "Makalelere Geri Dön" : "Back to Articles"}
        </motion.button>

        <div className="flex gap-8 max-w-7xl mx-auto">
          {/* Table of Contents - Left Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <TableOfContents content={article.content} />
          </aside>

          {/* Main Article Content */}
          <article className="flex-1 min-w-0 max-w-4xl">
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-6">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>
                    {new Date(article.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{article.read_time} min read</span>
                </div>
              </div>

              {/* Share Button */}
              <div className="mb-6">
                <ShareButton
                  title={article.title}
                  url={`/articles/${article.slug}`}
                  description={article.excerpt}
                />
              </div>
            </motion.header>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-red max-w-none"
            >
              <div className="bg-gray-900/50 border border-red-900/20 rounded-xl p-8 mb-8">
                <p className="text-lg text-gray-300 leading-relaxed">
                  {article.excerpt}
                </p>
              </div>

              <div className="text-gray-300 leading-relaxed space-y-6">
                <ReactMarkdown
                  rehypePlugins={[rehypeSlug]}
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1
                        className="text-3xl font-bold mt-8 mb-4 text-white"
                        {...props}
                      />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2
                        className="text-2xl font-bold mt-8 mb-4 text-white"
                        {...props}
                      />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3
                        className="text-xl font-bold mt-6 mb-3 text-white"
                        {...props}
                      />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4
                        className="text-lg font-bold mt-6 mb-3 text-white"
                        {...props}
                      />
                    ),
                    h5: ({ node, ...props }) => (
                      <h5
                        className="text-base font-bold mt-4 mb-2 text-white"
                        {...props}
                      />
                    ),
                    h6: ({ node, ...props }) => (
                      <h6
                        className="text-sm font-bold mt-4 mb-2 text-white"
                        {...props}
                      />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4 leading-relaxed" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul
                        className="list-disc list-inside mb-4 space-y-2 ml-4"
                        {...props}
                      />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol
                        className="list-decimal list-inside mb-4 space-y-2 ml-4"
                        {...props}
                      />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="ml-2" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a
                        className="text-red-600 hover:text-red-500 underline"
                        {...props}
                      />
                    ),
                    code: ({ node, inline, ...props }: any) =>
                      inline ? (
                        <code
                          className="bg-gray-800 text-red-500 px-2 py-1 rounded text-sm font-mono"
                          {...props}
                        />
                      ) : (
                        <code
                          className="block bg-gray-800 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4"
                          {...props}
                        />
                      ),
                    pre: ({ node, ...props }) => (
                      <pre
                        className="bg-gray-800 rounded-lg overflow-x-auto mb-4"
                        {...props}
                      />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote
                        className="border-l-4 border-red-600 pl-4 italic text-gray-400 mb-4"
                        {...props}
                      />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr className="border-gray-800 my-8" {...props} />
                    ),
                    table: ({ node, ...props }) => (
                      <div className="overflow-x-auto mb-4">
                        <table
                          className="min-w-full border border-gray-800"
                          {...props}
                        />
                      </div>
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        className="border border-gray-800 px-4 py-2 bg-gray-900 text-left font-bold"
                        {...props}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        className="border border-gray-800 px-4 py-2"
                        {...props}
                      />
                    ),
                  }}
                >
                  {article.content}
                </ReactMarkdown>
              </div>
            </motion.div>
          </article>
        </div>
      </div>
    </div>
  );
}
